import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Gauge, AlertTriangle, Zap, Route as RouteIcon, Clock,
  TrendingDown, ChevronRight, X, Map as MapIcon, Bell, Settings as SettingsIcon
} from "lucide-react";
import DrivingHeatmap from "@/components/DrivingHeatmap";

const RISK_BADGE = {
  Excellent: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Good:      "bg-blue-100    text-blue-700    border-blue-300",
  Caution:   "bg-amber-100   text-amber-700   border-amber-300",
  Reckless:  "bg-red-100     text-red-700     border-red-300",
};

const EVENT_LABEL = {
  harsh_brake: "Harsh Braking",
  rapid_accel: "Rapid Acceleration",
  sharp_turn:  "Sharp Turn",
};

const EVENT_ICON = {
  harsh_brake: TrendingDown,
  rapid_accel: Zap,
  sharp_turn:  AlertTriangle,
};

function ScoreRing({ score }) {
  const color = score >= 90 ? "#10b981" : score >= 75 ? "#3b82f6"
              : score >= 60 ? "#f59e0b" : "#ef4444";
  const circumference = 2 * Math.PI * 26;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-16 h-16 flex items-center justify-center">
      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 60 60">
        <circle cx="30" cy="30" r="26" stroke="#e4e4e7" strokeWidth="4" fill="none" />
        <circle cx="30" cy="30" r="26" stroke={color} strokeWidth="4" fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" />
      </svg>
      <span className="absolute text-sm font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone = "zinc" }) {
  const tones = {
    zinc: "text-zinc-500",
    red: "text-red-500",
    amber: "text-amber-500",
    emerald: "text-emerald-500",
    blue: "text-blue-500",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</span>
      </div>
      <p className="text-2xl font-heading font-bold text-zinc-900">{value}</p>
    </div>
  );
}

function VendorDrillDown({ vendorId, name, period, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const days = parseInt(period);
        const from = new Date(Date.now() - days * 86400000).toISOString();
        const to = new Date().toISOString();
        const { data } = await API.get(
          `/management/driving-analytics/${vendorId}?date_from=${from}&date_to=${to}`,
        );
        if (active) setData(data);
      } catch (err) { console.error(err); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [vendorId, period]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose} data-testid="driving-drill-overlay">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()} data-testid="driving-drill-panel">
        <div className="sticky top-0 bg-white border-b border-zinc-200 p-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-heading font-bold text-zinc-900">{name}</h3>
            <p className="text-xs text-zinc-500">Driving safety detail · last {period} days</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-100"
            data-testid="driving-drill-close">
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && data && (
          <div className="p-4 space-y-4">
            {/* Score & risk */}
            <div className="flex items-center gap-4 bg-zinc-50 rounded-xl p-4">
              <ScoreRing score={data.summary.safety_score} />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Safety Score</p>
                <p className="text-xl font-heading font-bold text-zinc-900">{data.summary.safety_score}/100</p>
                <Badge className={`mt-1 text-[10px] border ${RISK_BADGE[data.summary.risk_level]}`}>
                  {data.summary.risk_level}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Distance</p>
                <p className="text-xl font-heading font-bold text-zinc-900">
                  {data.summary.total_distance_miles} mi
                </p>
                <p className="text-[10px] text-zinc-400">{data.summary.jobs_analyzed} jobs analyzed</p>
              </div>
            </div>

            {/* Per-event counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatTile icon={Gauge}        label="Speeding"   value={data.summary.speeding_events}   tone="red" />
              <StatTile icon={TrendingDown} label="Harsh Brake" value={data.summary.harsh_brake_events} tone="amber" />
              <StatTile icon={Zap}          label="Rapid Accel" value={data.summary.rapid_accel_events} tone="amber" />
              <StatTile icon={AlertTriangle} label="Sharp Turn" value={data.summary.sharp_turn_events}  tone="red" />
            </div>

            {/* Job breakdown */}
            <div>
              <h4 className="text-xs font-heading font-bold text-zinc-700 uppercase tracking-wider mb-2">
                Per-Job Breakdown
              </h4>
              {data.jobs.length === 0 && (
                <p className="text-xs text-zinc-400 italic py-3">No tracked drives in this period.</p>
              )}
              <div className="space-y-2" data-testid="drill-jobs-list">
                {data.jobs.map(j => (
                  <div key={j.request_id} className="border border-zinc-200 rounded-lg p-3"
                    data-testid={`drill-job-${j.request_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-bold text-zinc-900">
                        {j.request_code || j.request_id.slice(0, 8)}
                      </p>
                      <Badge className={`text-[10px] border ${RISK_BADGE[j.analytics.risk_level]}`}>
                        {j.analytics.risk_level} · {j.analytics.safety_score}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-zinc-500 mb-2">
                      {j.service_type_label || "Job"} · {j.customer_name || "—"}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                      <div>
                        <span className="text-zinc-400">Distance</span>{" "}
                        <span className="font-bold text-zinc-700">{j.analytics.total_distance_miles} mi</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Max</span>{" "}
                        <span className="font-bold text-zinc-700">{j.analytics.max_speed_mph} mph</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Speeding</span>{" "}
                        <span className="font-bold text-red-600">{j.analytics.speeding_samples}</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Events</span>{" "}
                        <span className="font-bold text-amber-600">{j.event_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DrivingSafety() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [sortBy, setSortBy] = useState("score_desc");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("rankings");
  const [recent, setRecent] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(period);
      const from = new Date(Date.now() - days * 86400000).toISOString();
      const to = new Date().toISOString();
      const { data } = await API.get(
        `/management/driving-analytics?date_from=${from}&date_to=${to}`,
      );
      setData(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Recent live events feed (refreshed every 15s while on this tab)
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await API.get("/management/driving-events/recent?limit=25");
        if (active) setRecent(data.events || []);
      } catch (e) { /* silent */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;

  const sorted = [...data.technicians].sort((a, b) => {
    if (sortBy === "score_desc") return b.safety_score - a.safety_score;
    if (sortBy === "score_asc") return a.safety_score - b.safety_score;
    if (sortBy === "distance") return b.total_distance_miles - a.total_distance_miles;
    if (sortBy === "events") {
      const evA = a.harsh_brake_events + a.rapid_accel_events + a.sharp_turn_events + a.speeding_events;
      const evB = b.harsh_brake_events + b.rapid_accel_events + b.sharp_turn_events + b.speeding_events;
      return evB - evA;
    }
    return 0;
  });

  return (
    <div className="space-y-6" data-testid="driving-safety-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-500" /> Driving Safety Dashboard
          </h1>
          <p className="text-sm text-zinc-500">
            Per-driver speed, braking, acceleration & cornering analysis
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/management/driving/settings"
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 inline-flex items-center gap-1"
            data-testid="open-settings-link">
            <SettingsIcon className="w-3.5 h-3.5" /> Rules
          </Link>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="px-3 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
            data-testid="driving-period">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
            data-testid="driving-sort">
            <option value="score_desc">Safety Score ▼</option>
            <option value="score_asc">Safety Score ▲ (riskiest first)</option>
            <option value="distance">Distance Driven</option>
            <option value="events">Event Count</option>
          </select>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-zinc-200 -mb-3">
        {[
          { id: "rankings",  label: "Rankings",     icon: Shield, badge: null },
          { id: "heatmap",   label: "Heat-map",     icon: MapIcon, badge: null },
          { id: "alerts",    label: "Live Alerts",  icon: Bell,   badge: recent.length || null },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
              tab === t.id ? "border-zinc-900 text-zinc-900"
                           : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}>
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            {t.badge ? (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "rankings" && <>

      {/* Fleet KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fleet-kpis">
        <StatTile icon={Shield}       label="Avg Fleet Score" value={data.fleet.avg_safety_score} tone="emerald" />
        <StatTile icon={RouteIcon}    label="Total Distance"  value={`${data.fleet.total_distance_miles} mi`} tone="blue" />
        <StatTile icon={Gauge}        label="Speeding Events" value={data.fleet.total_speeding_events} tone="red" />
        <StatTile icon={AlertTriangle} label="Harsh Events"   value={
          data.fleet.total_harsh_brake_events + data.fleet.total_rapid_accel_events +
          data.fleet.total_sharp_turn_events
        } tone="amber" />
      </div>

      {/* Risk distribution */}
      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <h3 className="text-xs font-heading font-bold text-zinc-700 uppercase tracking-wider mb-3">
            Risk Distribution
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "Excellent", count: data.fleet.excellent_drivers, color: "bg-emerald-500", text: "text-emerald-700" },
              { label: "Good",      count: data.fleet.good_drivers,      color: "bg-blue-500",    text: "text-blue-700" },
              { label: "Caution",   count: data.fleet.caution_drivers,   color: "bg-amber-500",   text: "text-amber-700" },
              { label: "Reckless",  count: data.fleet.reckless_drivers,  color: "bg-red-500",     text: "text-red-700" },
            ].map(r => (
              <div key={r.label} className="flex flex-col items-center"
                data-testid={`risk-${r.label.toLowerCase()}`}>
                <div className={`w-12 h-12 rounded-full ${r.color} text-white flex items-center justify-center font-bold text-lg`}>
                  {r.count}
                </div>
                <p className={`text-[11px] font-bold mt-1 ${r.text}`}>{r.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Technician table */}
      <Card className="border border-zinc-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="driving-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500">Driver</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-center">Score</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-center">Risk</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Distance</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Max mph</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Speeding</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Brake</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Accel</th>
                  <th className="py-2.5 px-3 text-[10px] font-bold uppercase text-zinc-500 text-right">Turns</th>
                  <th className="py-2.5 px-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(t => (
                  <tr key={t.vendor_id}
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => t.jobs_analyzed > 0 && setSelected(t)}
                    data-testid={`driver-row-${t.vendor_id}`}>
                    <td className="py-3 px-3 font-medium text-zinc-800">
                      {t.name}
                      {t.jobs_analyzed === 0 && (
                        <span className="ml-2 text-[10px] text-zinc-400">(no drives)</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`inline-block font-bold ${
                        t.safety_score >= 90 ? "text-emerald-600"
                        : t.safety_score >= 75 ? "text-blue-600"
                        : t.safety_score >= 60 ? "text-amber-600" : "text-red-600"
                      }`}>{t.safety_score}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Badge className={`text-[10px] border ${RISK_BADGE[t.risk_level]}`}>
                        {t.risk_level}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-right text-zinc-600">{t.total_distance_miles}</td>
                    <td className="py-3 px-3 text-right text-zinc-600">{t.max_speed_mph}</td>
                    <td className={`py-3 px-3 text-right font-medium ${t.speeding_events ? "text-red-600" : "text-zinc-400"}`}>
                      {t.speeding_events}
                    </td>
                    <td className={`py-3 px-3 text-right font-medium ${t.harsh_brake_events ? "text-amber-600" : "text-zinc-400"}`}>
                      {t.harsh_brake_events}
                    </td>
                    <td className={`py-3 px-3 text-right font-medium ${t.rapid_accel_events ? "text-amber-600" : "text-zinc-400"}`}>
                      {t.rapid_accel_events}
                    </td>
                    <td className={`py-3 px-3 text-right font-medium ${t.sharp_turn_events ? "text-red-600" : "text-zinc-400"}`}>
                      {t.sharp_turn_events}
                    </td>
                    <td className="py-3 px-3 text-zinc-400">
                      {t.jobs_analyzed > 0 && <ChevronRight className="w-3.5 h-3.5" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-[11px] text-zinc-400">
        <Clock className="w-3 h-3 inline mr-1" />
        Thresholds are configurable in <Link to="/management/driving/settings" className="underline">Driving Safety Rules</Link>.
      </p>

      </>}

      {tab === "heatmap" && (
        <Card className="border border-zinc-200" data-testid="heatmap-card">
          <CardContent className="p-4">
            <DrivingHeatmap days={parseInt(period)} />
          </CardContent>
        </Card>
      )}

      {tab === "alerts" && (
        <Card className="border border-zinc-200" data-testid="alerts-card">
          <CardContent className="p-4">
            <h3 className="text-xs font-heading font-bold text-zinc-700 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Bell className="w-3.5 h-3.5 text-amber-500" /> Live Event Feed
              <span className="text-[10px] font-normal text-zinc-400">refreshes every 15s</span>
            </h3>
            {recent.length === 0 && (
              <p className="text-xs text-zinc-400 italic py-3">
                No flagged events yet. They will appear here moments after a technician triggers one.
              </p>
            )}
            <ul className="divide-y divide-zinc-100" data-testid="alerts-list">
              {recent.map(e => {
                const meta = ({
                  harsh_brake:   { color: "text-amber-600",  bg: "bg-amber-50",  label: "Harsh Brake" },
                  rapid_accel:   { color: "text-orange-600", bg: "bg-orange-50", label: "Rapid Accel" },
                  sharp_turn:    { color: "text-red-600",    bg: "bg-red-50",    label: "Sharp Turn" },
                  extreme_speed: { color: "text-red-700",    bg: "bg-red-100",   label: "Extreme Speed" },
                })[e.type] || { color: "text-zinc-600", bg: "bg-zinc-50", label: e.type };
                let detail = "";
                if (e.type === "harsh_brake") detail = `${e.from_speed} → ${e.to_speed} mph (-${e.drop})`;
                else if (e.type === "rapid_accel") detail = `${e.from_speed} → ${e.to_speed} mph (+${e.rise})`;
                else if (e.type === "sharp_turn") detail = `${e.heading_change}° at ${e.speed} mph`;
                else detail = `${e.speed} mph`;
                return (
                  <li key={e.id} className="flex items-center gap-3 py-2.5"
                    data-testid={`alert-${e.id}`}>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${meta.bg}`}>
                      <AlertTriangle className={`w-4 h-4 ${meta.color}`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-zinc-900">{e.vendor_name}</p>
                      <p className="text-[11px] text-zinc-500">
                        <span className={`font-bold ${meta.color}`}>{meta.label}</span> · {detail}
                      </p>
                    </div>
                    <span className="text-[10px] text-zinc-400">
                      {e.created_at ? new Date(e.created_at).toLocaleString([], {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      }) : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {selected && (
        <VendorDrillDown vendorId={selected.vendor_id} name={selected.name}
          period={period} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
